import { getActualItemCountForMenu } from "../components/preOrders/preOrderUtils";

interface FixedPriceItem {
  quantity?: number;
  sub_type?: string;
  for_name_index?: number;
}

interface MenuContainer extends FixedPriceItem {
  id?: string;
  type?: string;
  items?: FixedPriceItem[];
}

interface FixedPriceAddition {
  menu: {
    fixed_price?: boolean;
    fixed_price_rules?: { max_items: number }[];
    max_per_sub_type?: number;
  };
  item: { sub_type?: string };
  quantity: number;
}

function matchesPerson(container: MenuContainer, person: number | undefined): boolean {
  if (container.for_name_index !== undefined || person === undefined) {
    return container.for_name_index === person;
  }
  return container.items?.some((item) => item.for_name_index === person) ?? false;
}

function canFitItem(items: FixedPriceItem[], addition: FixedPriceAddition): boolean {
  const total = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  const subTypeTotal = items
    .filter((item) => item.sub_type === addition.item.sub_type)
    .reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  if (addition.menu.max_per_sub_type && addition.item.sub_type && subTypeTotal >= addition.menu.max_per_sub_type) {
    return false;
  }
  return addition.menu.fixed_price_rules?.some((rule) => rule.max_items >= total + 1) ?? false;
}

function canAddWithinContainerLimit(containers: MenuContainer[], max: number, addition: FixedPriceAddition): boolean {
  const groups = containers.map((container) => [...(container.items ?? [])]);
  for (let unit = 0; unit < addition.quantity; unit++) {
    let group = groups.find((items) => canFitItem(items, addition));
    if (!group) {
      if (groups.length >= max) return false;
      group = [];
      groups.push(group);
    }
    group.push({ sub_type: addition.item.sub_type, quantity: 1 });
  }
  return true;
}

/**
 * Calculates a numeric limit value from a raw limit setting.
 * Supports absolute numbers and percentage strings (e.g. "50%") relative to guestCount.
 *
 * @param limit - The raw limit value (number, percentage string, or undefined)
 * @param guestCount - The number of guests used for percentage calculations
 * @returns The resolved numeric limit, or undefined if no limit is set
 */
export function calculateLimitValue(limit: string | number | undefined, guestCount?: number): number | undefined {
  if (!limit) return undefined;

  if (guestCount && typeof limit === "string" && limit.includes("%")) {
    const percentage = Number.parseFloat(limit.replace("%", "")) / 100;
    return Math.ceil(guestCount * percentage);
  }

  return typeof limit === "number" ? limit : undefined;
}

/**
 * Validates whether a person has reached the maximum number of items
 * allowed per person for a given menu.
 *
 * @param menuId - The menu ID to validate against
 * @param menuSettings - The menu settings containing max_per_person
 * @param currentItems - Current basket items
 * @param orderingFor - Index of the person currently ordering
 * @returns Validation result with valid flag and optional message
 */
export function validateMaxPerPersonLimit(
  menuId: string,
  menuSettings: { max_per_person?: number },
  currentItems: MenuContainer[],
  orderingFor: number | undefined,
  addition?: FixedPriceAddition,
): { valid: boolean; message?: string } {
  if (!menuSettings.max_per_person || !addition?.menu.fixed_price) {
    return { valid: true };
  }

  const personMenus = currentItems.filter(
    (item) => item.id === menuId && item.type === "menu" && matchesPerson(item, orderingFor),
  );

  if (!canAddWithinContainerLimit(personMenus, menuSettings.max_per_person, addition)) {
    return {
      valid: false,
      message: `You can only add ${menuSettings.max_per_person} of this menu per person. Change who you're ordering for to add more.`,
    };
  }

  return { valid: true };
}

/**
 * Validates whether adding items would exceed the menu's item limits.
 *
 * @param menuId - The menu ID to validate against
 * @param adjustedQuantity - Quantity to add (already multiplied)
 * @param menuSettings - The menu settings containing limit_preorders
 * @param currentItems - Current basket items
 * @param guestCount - Number of guests in the booking
 * @param menuName - Display name of the menu (for error messages)
 * @returns Validation result with valid flag and optional message
 */
export function validateItemLimits(
  menuId: string,
  adjustedQuantity: number,
  menuSettings: any,
  currentItems: any[],
  guestCount?: number,
  menuName?: string,
): { valid: boolean; message?: string } {
  // Use getActualItemCountForMenu to properly count nested items in fixed price menus
  const currentCount = getActualItemCountForMenu(currentItems, menuId);
  const maxItems = calculateLimitValue(menuSettings?.limit_preorders?.max, guestCount);
  const newTotal = currentCount + adjustedQuantity;
  const displayMenuName = menuName ?? "";
  const excess = maxItems ? newTotal - maxItems : 0;

  if (excess > 0 && maxItems) {
    return {
      valid: false,
      message: `You have ${currentCount} items added to the ${displayMenuName} menu. You can not add any more items.`,
    };
  }

  return { valid: true };
}

/**
 * Validates whether an item can be added to a specific menu,
 * checking both per-person and overall item limits.
 *
 * @param menuId - The menu ID to add the item to
 * @param quantity - The quantity being added
 * @param processedMenusData - Map of menu ID to processed menu data
 * @param currentItems - Current basket items
 * @param guestCount - Number of guests in the booking
 * @param orderingFor - Index of the person currently ordering
 * @returns Validation result with valid flag and optional message
 */
export function validateItemAddition(
  menuId: string,
  quantity: number,
  processedMenusData: { [menuId: string]: any },
  currentItems: any[],
  guestCount: number | undefined,
  orderingFor: number | undefined,
  addition?: FixedPriceAddition,
): { valid: boolean; message?: string } {
  const menuData = processedMenusData[menuId];
  if (!menuData?.menuSettings) {
    return { valid: true };
  }

  const menuSettings = menuData.menuSettings;
  const adjustedQuantity = quantity * (menuSettings.item_multiplier ?? 1);

  const maxPerPersonValidation = validateMaxPerPersonLimit(menuId, menuSettings, currentItems, orderingFor, addition);
  if (!maxPerPersonValidation.valid) {
    return maxPerPersonValidation;
  }

  const menuName = menuData?.menuInfo?.name;
  const itemLimitsValidation = validateItemLimits(
    menuId,
    adjustedQuantity,
    menuSettings,
    currentItems,
    guestCount,
    menuName,
  );
  return itemLimitsValidation;
}

interface MenuEntry {
  id: string;
  name: string;
  items: any[];
  minimum_preorder_amount?: number;
  minimum_preorder_amount_per?: string;
  [key: string]: any;
}

/**
 * Calculates the total spend for each menu from the current basket items.
 *
 * @param items - Current basket items
 * @returns A map of menu ID to total spend
 */
export function getSpendByMenu(items: any[]): Record<string, number> {
  const spendByMenuId: Record<string, number> = {};

  for (const item of items) {
    const menuId = item.type === "menu" ? item.id : item.menu_id;
    if (!menuId) continue;
    spendByMenuId[menuId] = (spendByMenuId[menuId] ?? 0) + (Number(item.total) || 0);
  }

  return spendByMenuId;
}

/**
 * Returns the required minimum spend total for a menu, accounting for
 * whether the minimum is per guest or per booking.
 *
 * @param menu - The menu entry containing minimum_preorder_amount settings
 * @param guestCount - Number of guests in the booking
 * @returns The resolved minimum spend total, or undefined if no minimum is set
 */
export function getMinimumSpendTotal(menu: MenuEntry, guestCount: number | undefined): number | undefined {
  if (!menu.minimum_preorder_amount) return undefined;

  const perType = menu.minimum_preorder_amount_per ?? "guest";
  if (perType === "guest" && guestCount !== undefined) {
    return guestCount * menu.minimum_preorder_amount;
  }

  return menu.minimum_preorder_amount;
}

export interface MinimumSpendError {
  menuId: string;
  menuName: string;
  requiredAmount: number;
  currentSpend: number;
}

/**
 * Validates minimum spend requirements across all menus and returns
 * per-menu error details for any that fall short.
 *
 * @param menus - Array of menu entries
 * @param currentItems - Current basket items
 * @param guestCount - Number of guests in the booking
 * @returns An array of minimum spend errors (empty if all requirements are met)
 */
export function getMinimumSpendErrors(
  menus: MenuEntry[],
  currentItems: any[],
  guestCount: number | undefined,
): MinimumSpendError[] {
  const spendByMenu = getSpendByMenu(currentItems);
  const errors: MinimumSpendError[] = [];

  for (const menu of menus) {
    const requiredAmount = getMinimumSpendTotal(menu, guestCount);
    if (!requiredAmount) continue;

    const currentSpend = spendByMenu[menu.id] ?? 0;
    if (currentSpend < requiredAmount) {
      errors.push({
        menuId: menu.id,
        menuName: menu.name,
        requiredAmount,
        currentSpend,
      });
    }
  }

  return errors;
}

/**
 * Checks whether all menus with a minimum spend requirement have been satisfied.
 *
 * @param menus - Array of menu entries
 * @param currentItems - Current basket items
 * @param guestCount - Number of guests in the booking
 * @returns true if all minimum spend requirements are met, false otherwise
 */
export function validateMinimumSpend(menus: MenuEntry[], currentItems: any[], guestCount: number | undefined): boolean {
  return getMinimumSpendErrors(menus, currentItems, guestCount).length === 0;
}

/**
 * Validates all menus to check whether the current item counts
 * satisfy each menu's min/max limit constraints.
 *
 * @param currentItems - Current basket items
 * @param guestCount - Number of guests in the booking
 * @param menus - Array of menu entries
 * @param processedMenusData - Map of menu ID to processed menu data
 * @returns true if all menus satisfy their limits, false otherwise
 */
export function validateAllMenus(
  currentItems: any[],
  guestCount: number | undefined,
  menus: MenuEntry[],
  processedMenusData: { [menuId: string]: any },
): boolean {
  for (const menu of menus) {
    const menuData = processedMenusData[menu.id];
    if (!menuData?.menuSettings?.limit_preorders) continue;

    const menuSettings = menuData.menuSettings;
    // Use getActualItemCountForMenu to properly count nested items in fixed price menus
    const currentCount = getActualItemCountForMenu(currentItems, menu.id);

    const minItems = calculateLimitValue(menuSettings.limit_preorders.min, guestCount);
    const maxItems = calculateLimitValue(menuSettings.limit_preorders.max, guestCount);
    // Check if current count violates min/max limits
    if (minItems && currentCount < minItems) {
      return false;
    }
    if (maxItems && currentCount > maxItems) {
      return false;
    }
  }

  // Check minimum spend requirements
  if (!validateMinimumSpend(menus, currentItems, guestCount)) {
    return false;
  }

  return true;
}
